import React, { useState } from 'react';
import { Button, Card, Collapse, Space, Typography, Alert, App } from 'antd';
import { ReloadOutlined, WarningOutlined, ExclamationCircleOutlined, InfoCircleOutlined, BugOutlined, CopyOutlined } from '@ant-design/icons';
import { parseTableError, formatTechnicalDetails } from '../../utils/parseTableError';
import type { ParsedTableError } from '../../utils/parseTableError';
import { openSupportWithError } from '../../utils/openSupportWithError';

const { Text, Paragraph } = Typography;

/** Панель с деталями ошибки — видна и в проде для копирования/отправки в поддержку */
const ErrorDetailsPanel: React.FC<{
  technicalDetails: ParsedTableError['technicalDetails'];
  errorId?: string;
  tableTitle?: string;
}> = ({ technicalDetails, errorId, tableTitle }) => {
  const { message: messageApi } = App.useApp();

  const getDetailsText = () => {
    const parts: string[] = [];
    if (errorId) parts.push(`Error ID: ${errorId}`);
    parts.push(`Время: ${new Date().toLocaleString('ru-RU')}`);
    parts.push(`Страница: ${window.location.pathname}`);
    if (tableTitle) parts.push(`Таблица: ${tableTitle}`);
    parts.push('');
    parts.push(formatTechnicalDetails(technicalDetails));
    return parts.join('\n');
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(getDetailsText());
      messageApi.success('Детали ошибки скопированы');
    } catch {
      // fallback для iframe/http
      const textarea = document.createElement('textarea');
      textarea.value = getDetailsText();
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      messageApi.success('Детали ошибки скопированы');
    }
  };

  return (
    <div style={{ width: '100%', marginTop: 16 }}>
      <Collapse
        ghost
        items={[
          {
            key: 'details',
            label: (
              <Space>
                <InfoCircleOutlined />
                <Text strong>Детали ошибки</Text>
              </Space>
            ),
            children: (
              <div style={{ padding: '8px 0' }}>
                <Paragraph
                  code
                  style={{
                    backgroundColor: '#f6f8fa',
                    padding: 12,
                    borderRadius: 4,
                    whiteSpace: 'pre-wrap',
                    fontSize: '12px',
                    maxHeight: '300px',
                    overflow: 'auto',
                    marginBottom: 8,
                  }}
                >
                  {formatTechnicalDetails(technicalDetails)}
                </Paragraph>
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={handleCopy}
                >
                  Скопировать детали
                </Button>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};

export interface TableErrorPlaceholderProps {
  error: Error | unknown;
  onRetry?: () => void;
  tableTitle?: string;
  height?: number | string;
  className?: string;
  errorId?: string;
}

export const TableErrorPlaceholder: React.FC<TableErrorPlaceholderProps> = ({
  error,
  onRetry,
  tableTitle,
  height = 'auto',
  className,
  errorId,
}) => {
  const [isRetrying, setIsRetrying] = useState(false);
  const parsedError: ParsedTableError = parseTableError(error);

  const handleRetry = async () => {
    if (!onRetry) return;
    
    setIsRetrying(true);
    try {
      await onRetry();
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('Retry failed:', err);
      }
    } finally {
      setTimeout(() => {
        setIsRetrying(false);
      }, 500);
    }
  };

  const getIcon = () => {
    if (parsedError.severity === 'warning') {
      return <WarningOutlined style={{ fontSize: 48, color: 'var(--color-warning)' }} />;
    }
    return <ExclamationCircleOutlined style={{ fontSize: 48, color: 'var(--color-error)' }} />;
  };

  const getAlertType = () => {
    return parsedError.severity === 'warning' ? 'warning' : 'error';
  };

  return (
    <Card
      className={className}
      style={{
        minHeight: typeof height === 'number' ? `${height}px` : height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: parsedError.severity === 'warning' ? '1px dashed #faad14' : '1px dashed #ff4d4f',
        backgroundColor: parsedError.severity === 'warning' ? '#fffbe6' : '#fff2f0',
      }}
      styles={{
        body: {
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 24px',
        }
      }}
    >
      <Space orientation="vertical" size="large" align="center" style={{ width: '100%', maxWidth: 600 }}>
        {getIcon()}
        <div style={{ textAlign: 'center' }}>
          <Text strong style={{ fontSize: 18, display: 'block', marginBottom: 8 }}>
            {parsedError.title}
          </Text>
          {tableTitle && (
            <Text type="secondary" style={{ fontSize: 14 }}>
              {tableTitle}
            </Text>
          )}
        </div>

        <Alert
          message={parsedError.message}
          type={getAlertType()}
          showIcon
          style={{ width: '100%' }}
        />

        <Space size="middle">
          {parsedError.canRetry && onRetry && (
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={handleRetry}
              loading={isRetrying}
              size="large"
            >
              Повторить
            </Button>
          )}
          
          <Button
            icon={<ReloadOutlined />}
            onClick={() => onRetry ? handleRetry() : window.location.reload()}
            size="large"
          >
            Обновить таблицу
          </Button>

          <Button
            icon={<BugOutlined />}
            onClick={() =>
              openSupportWithError({
                tableTitle,
                errorMessage: parsedError.message,
                errorId,
                httpStatus: parsedError.technicalDetails?.statusCode,
                correlationId: parsedError.technicalDetails?.correlationId,
                operationId: parsedError.technicalDetails?.operationId,
                stack: parsedError.technicalDetails?.stack,
              })
            }
            size="large"
          >
            Сообщить о проблеме
          </Button>
        </Space>

        {parsedError.technicalDetails && (
          <ErrorDetailsPanel
            technicalDetails={parsedError.technicalDetails}
            errorId={errorId}
            tableTitle={tableTitle}
          />
        )}
      </Space>
    </Card>
  );
};


